import React from 'react';

import {
  VariantSettingsSlug,
  variantSettingsRouteConfig,
} from 'src/app/graph/routes';
import { L3SettingsMenu } from 'src/components/l3SettingsMenu/L3SettingsMenu';
import { useCurrentPlan } from 'src/hooks/currentPlanV2Migration';
import { useCapabilities } from 'src/hooks/useCapabilities';
import { useCheckPlanTypes } from 'src/hooks/useCheckPlanTypes';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import { useRouteParams } from 'src/hooks/useRouteParams';

import { GraphRef } from '../hooks/useGraphRef';

type CONTEXTS = 'dedicated' | 'cloud' | 'default';
/**
 * The static list of variant settings menu items.
 */
const variantSettingsMenuItems: Array<
  { showInContexts: CONTEXTS[] } & React.ComponentProps<
    typeof L3SettingsMenu<VariantSettingsSlug>
  >['settingsMenuItems'][number]
> = [
  {
    slug: 'general',
    title: 'General',
    showInContexts: ['dedicated', 'cloud', 'default'],
  },
  {
    slug: 'routing',
    title: 'Cloud Router',
    showInContexts: ['cloud'],
  },
  {
    slug: 'contracts',
    title: 'Contracts',
    showInContexts: ['dedicated', 'default'],
  },
  {
    slug: 'explorer',
    title: 'Explorer',
    showInContexts: ['dedicated', 'cloud', 'default'],
  },
  {
    slug: 'checks',
    title: 'Checks',
    showInContexts: ['cloud', 'default', 'dedicated'],
    defaultNested: 'checks_operations',
    nested: [
      { slug: 'checks_operations', title: 'Operations' },
      { slug: 'checks_linter', title: 'Linter' },
      { slug: 'checks_proposals', title: 'Proposals' },
      { slug: 'checks_custom_checks', title: 'Custom checks' },
    ],
  },
];

/**
 * Component that renders the variant settings menu based on certain conditions.
 *
 * - If the customer is a dedicated customer, only the 'General', 'Contracts', and 'Explorer' menu items are displayed.
 * - If the graph is a cloud supergraph, all menu items except 'Contracts' are displayed.
 * - Otherwise, all menu items except 'Routing' are displayed.
 *
 * @param {Object} props - The properties for the component.
 * @param {GraphRef} props.graphRef - The graph reference object.
 * @param {boolean} props.isCloudSupergraph - Indicates if the graph is a cloud supergraph.
 * @returns {JSX.Element} The rendered settings menu component.
 */
export const VariantSettingsMenu = ({
  graphRef,
  isCloudSupergraph,
}: {
  graphRef: GraphRef;
  isCloudSupergraph: boolean;
}) => {
  const { tab } = useRouteParams(variantSettingsRouteConfig);

  const [accountId] = useCurrentAccountId();
  const currentPlanResult = useCurrentPlan({
    skip: !accountId,
    accountId: accountId as string,
  });
  const { isDedicatedPlan } = useCheckPlanTypes({
    tier: currentPlanResult?.currentPlan?.tier,
    kind: currentPlanResult?.currentPlan?.kind,
  });
  const { capabilities } = useCapabilities();

  /**
   * Helper function to apply the route configuration.
   *
   * @param {VariantSettingsSlug} tabName - The tab name.
   * @returns {string} The URL for the given tab.
   */
  const applyRouteConfig = (tabName: VariantSettingsSlug) => {
    return variantSettingsRouteConfig.location({ ...graphRef, tab: tabName });
  };

  const visibleVariantSettingsMenuItems = variantSettingsMenuItems.filter(
    ({ showInContexts, slug }) => {
      if (!capabilities?.webhooks && slug === 'checks_custom_checks') {
        return false;
      } else if (!capabilities?.proposals && slug === 'checks_proposals') {
        return false;
      } else if (isDedicatedPlan) {
        return showInContexts.includes('dedicated');
      } else if (isCloudSupergraph) {
        return showInContexts.includes('cloud');
      } else {
        return showInContexts.includes('default');
      }
    },
  );

  return (
    <L3SettingsMenu
      settingsMenuItems={visibleVariantSettingsMenuItems}
      applyRouteConfig={applyRouteConfig}
      defaultSlug="general"
      currentTab={tab}
    />
  );
};
